/**
 * Marqueur de variante A/B — voir handoffs/2026-09-02-handoff-srm-rollout-ab.md
 *
 * Shopify Rollouts n'expose aucun identifiant de variante à GA4, aux pixels ou
 * aux commandes. Ce module l'ajoute lui-même, par deux canaux complémentaires :
 *
 *   1. un événement personnalisé Web Pixel (`adre:ab_variant_assigned`), qui permet
 *      de mesurer le split côté SESSIONS — c'est lui qui sert à surveiller le SRM ;
 *   2. un attribut de panier (`AB Variant`), qui rattache les COMMANDES à leur bras.
 *
 * Le thème n'utilise ni GTM ni dataLayer : GA4 arrive par le canal Google &
 * YouTube, donc par les Web Pixels. Un `dataLayer.push` n'irait nulle part.
 *
 * L'attribut de panier n'est posé qu'AU PREMIER ajout au panier, et une seule
 * fois par session (sessionStorage). Le poser à chaque pageview créerait un
 * panier pour chaque visiteur et détruirait les statistiques d'abandon.
 */

import { StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const VARIANT = window.__abVariant;
const THEME_ID = window.__abThemeId;
const STORAGE_KEY = 'fred:ab-variant-tagged';

/** Ne rien faire si le bootstrap Liquid n'a pas tourné. */
if (VARIANT) {
  publishPixelEvent();
  listenForFirstCartUpdate();
}

/**
 * Canal 1 — événement Web Pixel, émis à chaque pageview.
 * Capté par un pixel personnalisé qui le relaie vers GA4.
 */
function publishPixelEvent() {
  try {
    window.Shopify?.analytics?.publish('adre:ab_variant_assigned', {
      ab_variant: VARIANT,
      ab_theme_id: String(THEME_ID),
    });
  } catch {
    // L'absence d'analytics ne doit jamais casser la page.
  }
}

/**
 * Canal 2 — attribut de panier, posé au premier ajout au panier de la session.
 */
function listenForFirstCartUpdate() {
  if (alreadyTagged()) return;

  document.addEventListener(
    StandardEvents.cartLinesUpdate,
    () => {
      if (alreadyTagged()) return;
      markTagged();
      tagCart();
    },
    { once: true }
  );
}

/** @returns {boolean} */
function alreadyTagged() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === VARIANT;
  } catch {
    // Navigation privée ou stockage bloqué : on retombe sur un drapeau mémoire.
    return window.__abTagged === true;
  }
}

function markTagged() {
  window.__abTagged = true;
  try {
    sessionStorage.setItem(STORAGE_KEY, VARIANT);
  } catch {
    // Sans stockage, le drapeau mémoire suffit pour la durée de la page.
  }
}

function tagCart() {
  // `Theme` est déclaré par `const` dans un <script> classique (snippets/scripts.liquid) :
  // c'est une liaison de portée script, visible par l'identifiant nu depuis un
  // module, mais JAMAIS exposée sur `window`. D'où le `typeof` plutôt qu'un
  // `window.Theme?.` — qui serait toujours undefined.
  const updateUrl =
    typeof Theme !== 'undefined' ? Theme.routes.cart_update_url : '/cart/update';

  // Même convention que cart-note.js / cart-discount.js : POST JSON sur
  // `cart_update_url` (l'endpoint accepte le JSON et répond en JSON).
  const config = fetchConfig('json', {
    body: JSON.stringify({ attributes: { 'AB Variant': VARIANT } }),
  });

  fetch(updateUrl, config).catch(() => {
    // Un échec de marquage ne doit pas empêcher l'ajout au panier.
  });
}
